import React, { useState, useEffect, useRef } from 'react';
import './DateRangePicker.css';

const DateRangePicker = ({ checkIn, checkOut, onDatesChange, minDate }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  const monthsScrollRef = useRef(null);

  const today = minDate ? new Date(minDate) : new Date();
  today.setHours(0, 0, 0, 0);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  // Initialize current month based on check-in date or today
  useEffect(() => {
    if (checkIn) {
      setCurrentMonth(new Date(checkIn));
    } else {
      setCurrentMonth(new Date());
    }
  }, [checkIn]);

  // Debug: Log when component renders
  useEffect(() => {
    console.log('📅 DateRangePicker rendered:', { showPicker, checkIn, checkOut, hasTriggerRef: !!triggerRef.current });
  }, [showPicker, checkIn, checkOut]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[d.getMonth()]} ${d.getDate()}`;
  };

  const getDateRangeText = () => {
    if (checkIn && checkOut) {
      return `${formatDate(checkIn)} — ${formatDate(checkOut)}`;
    }
    return 'Check-in date — Check-out date';
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Convert to Monday = 0, Sunday = 6 format
    const startingDayOfWeek = firstDay.getDay();
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    return { daysInMonth, startingDayOfWeek: adjustedStartDay, year, month };
  };

  const isDateDisabled = (date) => {
    return date < today;
  };

  const isDateInRange = (date) => {
    if (!checkIn || !checkOut) return false;
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    return date >= checkInDate && date <= checkOutDate;
  };

  const isDateSelected = (date) => {
    if (!checkIn && !checkOut) return false;
    const dateStr = date.toISOString().split('T')[0];
    return dateStr === checkIn || dateStr === checkOut;
  };

  const isDateHovered = (date) => {
    if (!hoverDate || !checkIn || checkOut) return false;
    const checkInDate = new Date(checkIn);
    const hoverDateObj = new Date(hoverDate);
    return date > checkInDate && date < hoverDateObj;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    const dateStr = date.toISOString().split('T')[0];

    if (!checkIn || (checkIn && checkOut)) {
      // Start new selection
      onDatesChange(dateStr, null);
    } else if (checkIn && !checkOut) {
      // Complete selection
      if (dateStr <= checkIn) {
        // If clicked date is before check-in, make it the new check-in
        onDatesChange(dateStr, null);
      } else {
        onDatesChange(checkIn, dateStr);
        setShowPicker(false);
      }
    }
  };

  const handleDateHover = (date) => {
    if (checkIn && !checkOut && !isDateDisabled(date)) {
      setHoverDate(date);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Generate months for flexible date selection (starting from current month, up to 12 months ahead)
  const getAvailableMonths = () => {
    const months = [];
    const startDate = new Date();
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
      months.push(monthDate);
    }
    return months;
  };

  const formatMonthYear = (date) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const toggleMonthSelection = (monthDate) => {
    const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`;
    setSelectedMonths(prev => {
      if (prev.length >= 3 && !prev.includes(monthKey)) {
        return prev; // Max 3 months
      }
      if (prev.includes(monthKey)) {
        return prev.filter(m => m !== monthKey);
      }
      return [...prev, monthKey];
    });
  };

  const isMonthSelected = (monthDate) => {
    const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`;
    return selectedMonths.includes(monthKey);
  };

  const handleFlexibleDatesSelect = () => {
    if (selectedDuration && selectedMonths.length > 0) {
      // Calculate dates based on duration and selected months
      // For now, we'll use the first selected month and calculate based on duration
      const firstMonth = selectedMonths[0].split('-');
      const year = parseInt(firstMonth[0]);
      const month = parseInt(firstMonth[1]);
      
      let daysToAdd = 0;
      if (selectedDuration === 'weekend') {
        daysToAdd = 2; // Friday to Sunday
      } else if (selectedDuration === 'week') {
        daysToAdd = 7;
      } else if (selectedDuration === 'month') {
        daysToAdd = 30;
      } else {
        daysToAdd = 7; // Default to a week for "Other"
      }
      
      const checkInDate = new Date(year, month, 1);
      const checkOutDate = new Date(year, month, 1 + daysToAdd);
      
      onDatesChange(
        checkInDate.toISOString().split('T')[0],
        checkOutDate.toISOString().split('T')[0]
      );
      setShowPicker(false);
    }
  };

  const renderCalendar = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(displayMonth);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    
    const days = [];
    
    // Add empty cells for days before the first day of the month (Monday = 0)
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push(date);
    }

    return (
      <div className="date-range-picker__calendar">
        <div className="date-range-picker__calendar-header">
          {monthOffset === 0 && (
            <button 
              type="button"
              className="date-range-picker__nav-btn"
              onClick={prevMonth}
            >
              ‹
            </button>
          )}
          <h3 className="date-range-picker__month-year">
            {monthNames[month]} {year}
          </h3>
          {monthOffset === 1 && (
            <button 
              type="button"
              className="date-range-picker__nav-btn date-range-picker__nav-btn--next"
              onClick={nextMonth}
            >
              ›
            </button>
          )}
        </div>
        
        <div className="date-range-picker__weekdays">
          {dayNames.map((day, index) => (
            <div key={index} className="date-range-picker__weekday">{day}</div>
          ))}
        </div>
        
        <div className="date-range-picker__days">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="date-range-picker__day date-range-picker__day--empty"></div>;
            }
            
            const dateStr = date.toISOString().split('T')[0];
            const disabled = isDateDisabled(date);
            const selected = isDateSelected(date);
            const inRange = isDateInRange(date) || isDateHovered(date);
            const isStart = dateStr === checkIn;
            const isEnd = dateStr === checkOut;
            
            return (
              <button
                key={index}
                type="button"
                className={`date-range-picker__day 
                  ${disabled ? 'date-range-picker__day--disabled' : ''}
                  ${selected ? 'date-range-picker__day--selected' : ''}
                  ${inRange ? 'date-range-picker__day--in-range' : ''}
                  ${isStart ? 'date-range-picker__day--start' : ''}
                  ${isEnd ? 'date-range-picker__day--end' : ''}
                `}
                onClick={() => handleDateClick(date)}
                onMouseEnter={() => handleDateHover(date)}
                disabled={disabled}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="date-range-picker" ref={pickerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="date-range-picker__trigger"
        onMouseDown={(e) => {
          console.log('📅 Date picker button mousedown');
          e.stopPropagation();
        }}
        onClick={(e) => {
          console.log('📅 Date picker button clicked!', { showPicker, checkIn, checkOut });
          e.preventDefault();
          e.stopPropagation();
          setShowPicker(prev => {
            const newValue = !prev;
            console.log('📅 Setting showPicker from', prev, 'to', newValue);
            return newValue;
          });
        }}
        onFocus={(e) => {
          console.log('📅 Date picker button focused');
        }}
        style={{
          position: 'relative',
          zIndex: 100,
          pointerEvents: 'auto'
        }}
      >
        {getDateRangeText() || 'Add dates'}
      </button>
      
      {showPicker && (
        <>
          <div 
            className="date-range-picker__backdrop"
            onClick={() => setShowPicker(false)}
          />
          <div 
            className="date-range-picker__dropdown"
            onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: '0',
            zIndex: 10000,
            display: 'block',
            visibility: 'visible',
            opacity: 1,
            overflow: 'visible'
          }}
          >
          <div className="date-range-picker__tabs">
            <button
              type="button"
              className={`date-range-picker__tab ${activeTab === 'calendar' ? 'date-range-picker__tab--active' : ''}`}
              onClick={() => setActiveTab('calendar')}
            >
              Calendar
            </button>
            <button
              type="button"
              className={`date-range-picker__tab ${activeTab === 'flexible' ? 'date-range-picker__tab--active' : ''}`}
              onClick={() => setActiveTab('flexible')}
            >
              I'm flexible
            </button>
          </div>
          
          {activeTab === 'calendar' && (
            <div className="date-range-picker__calendars">
              {renderCalendar(0)}
              {renderCalendar(1)}
            </div>
          )}
          
          {activeTab === 'flexible' && (
            <div className="date-range-picker__flexible">
              <div className="date-range-picker__flexible-section">
                <h3 className="date-range-picker__flexible-title">How long do you want to stay?</h3>
                <div className="date-range-picker__duration-options">
                  <label className="date-range-picker__duration-option">
                    <input
                      type="radio"
                      name="duration"
                      value="weekend"
                      checked={selectedDuration === 'weekend'}
                      onChange={(e) => setSelectedDuration(e.target.value)}
                    />
                    <span>A weekend</span>
                  </label>
                  <label className="date-range-picker__duration-option">
                    <input
                      type="radio"
                      name="duration"
                      value="week"
                      checked={selectedDuration === 'week'}
                      onChange={(e) => setSelectedDuration(e.target.value)}
                    />
                    <span>A week</span>
                  </label>
                  <label className="date-range-picker__duration-option">
                    <input
                      type="radio"
                      name="duration"
                      value="month"
                      checked={selectedDuration === 'month'}
                      onChange={(e) => setSelectedDuration(e.target.value)}
                    />
                    <span>A month</span>
                  </label>
                  <label className="date-range-picker__duration-option">
                    <input
                      type="radio"
                      name="duration"
                      value="other"
                      checked={selectedDuration === 'other'}
                      onChange={(e) => setSelectedDuration(e.target.value)}
                    />
                    <span>Other</span>
                  </label>
                </div>
              </div>

              <div className="date-range-picker__flexible-section">
                <h3 className="date-range-picker__flexible-title">When do you want to go?</h3>
                <p className="date-range-picker__flexible-subtitle">Select up to 3 months</p>
                <div className="date-range-picker__months-container">
                  <div 
                    className="date-range-picker__months-scroll"
                    ref={monthsScrollRef}
                  >
                    {getAvailableMonths().map((monthDate, index) => {
                      const isSelected = isMonthSelected(monthDate);
                      return (
                        <button
                          key={index}
                          type="button"
                          className={`date-range-picker__month-card ${isSelected ? 'date-range-picker__month-card--selected' : ''}`}
                          onClick={() => toggleMonthSelection(monthDate)}
                        >
                          <div className="date-range-picker__month-icon">📅</div>
                          <div className="date-range-picker__month-text">{formatMonthYear(monthDate)}</div>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="date-range-picker__scroll-btn"
                    onClick={() => {
                      if (monthsScrollRef.current) {
                        monthsScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
                      }
                    }}
                    aria-label="Scroll months"
                  >
                    ›
                  </button>
                </div>
              </div>

              <div className="date-range-picker__flexible-footer">
                <span className="date-range-picker__flexible-hint">Select days and months</span>
                <button
                  type="button"
                  className="date-range-picker__select-dates-btn"
                  onClick={handleFlexibleDatesSelect}
                  disabled={!selectedDuration || selectedMonths.length === 0}
                >
                  Select dates
                </button>
              </div>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
